
import { api } from './api';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { errorSafe, LoggedError } from 'app/infrastructure/redux';
import {
  IncreaseExecutionCounter,
  RequestContainerDefinitions,
  ReceiveContainerDefinitions,
  RequestMainFavourite,
  ReceiveMainFavourite,
  FailedMainFavourite,
  ClearMainFavourite,
  FailedContainerDefinitions
} from './action-types';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import * as routerActions from 'app/area/shared/navigation';
import { DrillDownModel } from 'app/components/favourite-layout/favourite/common/drill-down-model';
import { LocationWithQuery } from 'app/components/enhancers/with-router';

/**
 *   "The only way to change the state tree is to emit an action, an object describing what happened."
 *
 *    ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
 *    They don't directly mutate state, but they can have external side-effects (such as loading data).
 */
export const actionCreators = {

  /**
   * Creates (if needed) and loads definition for dashboard main favourite.
   * 
   * @param {number} dashboardType - dashboard favourite type - used to generate new favourite when mainFavouriteId is not provided
   * @param {number} mainFavouriteId - main dashboard favourite id - used to get dashboard favourite definition
   * @param {boolean} readonlyDashboard - if dashboard is readonly it will not update location with favourite id
   * @param {{ pathname: string; query: any }} location - browser location object - used to redirects
   */
  createMainFavourite: (dashboardType: number, mainFavouriteId: number, readonlyDashboard: boolean,
    location: LocationWithQuery):
    ActionDispatching<any, ApplicationState> => async (dispatch, getState) => {
      try {
        dispatch(new RequestMainFavourite());
        const utcOffset = getState().app.settings.utcOffset;
        let favouriteId = mainFavouriteId;
        if (!mainFavouriteId && dashboardType) {
          const mainFavourite = await api.createMainFavourite(dashboardType, { utcOffset });
          favouriteId = mainFavourite.data;
          const userMessages = mainFavourite?.messages?.userMessages;

          !!userMessages?.length
            && userMessages.forEach(err =>
              dispatch(new ErrorNotification(err.message))
            );
        }

        if (!readonlyDashboard && !mainFavouriteId) {
          routerActions.replace({ pathname: location.pathname + '/' + favouriteId });
        }

        dispatch(new ReceiveMainFavourite(favouriteId));
      } catch (err) {
        dispatch(new LoggedError(err));
        dispatch(new FailedMainFavourite(mainFavouriteId));
        routerActions.toStartupView();
      }
    },

  /**
   * Loads dashboard container favourites definitions.
   * 
   * @param {number} dashboardTypeId - the dashboard type id to get definition for.
   * @param {number} layoutTypeContextId - the context that this layout will be shown in.
   */
  loadContainerDefinitions: (dashboardTypeId: number, layoutTypeContextId: number):
    ActionDispatching<any, ApplicationState> => errorSafe(
      async (dispatch, getState) => {

        dispatch(new RequestContainerDefinitions(dashboardTypeId));

        try {
          const typeDefinition = await api.dashboardLayoutDefinition(dashboardTypeId, layoutTypeContextId);
          typeDefinition.dashboardFavourites.forEach(x => x.id = x.favourite);
          dispatch(new ReceiveContainerDefinitions(dashboardTypeId, typeDefinition));

        } catch (err) {
          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          dispatch(new FailedContainerDefinitions(dashboardTypeId));
          routerActions.toStartupView();
        }
      }),

  /**
   * Executes dashboard favourite.
   * 
   * @param {FavouriteDefinition} definition - container favourite definition to be executed
   * @param {number} mainFavouriteId - main dashboard favourite id
   * @param {number} containerFavouriteId - container favourite id
   */
  executeDashboard: ():
    ActionDispatching<any, ApplicationState> => errorSafe(
      (dispatch, getState) => {
        dispatch(new IncreaseExecutionCounter());
      }),

  /**
   * Drilldown from dashboard
   * 
   * @param {number} mainFavouriteId - main dashboard favourite id
   * @param {number} containerFavouriteId - container favourite id
   * @param {DrillDownModel} drillDownModel - parameters to drilldown
   */
  drillDownToCaseListing: (mainFavouriteId: number, containerFavouriteId: number, drillDownModel: DrillDownModel):
    ActionDispatching<any, ApplicationState> => errorSafe(
      async () => {
        const result = await api.drillDown(mainFavouriteId, containerFavouriteId, drillDownModel);

        //TODO: Deal with errors by dispatching action to dashboard or in case listing component or in a different route?
        routerActions.toCaseListing(result.favourite);
      }),
  clearMainFavourite: ():
    ActionDispatching<any, ApplicationState> => errorSafe(
      async (dispatch, getState) => {
        dispatch(new ClearMainFavourite());
      })
};
